/**
 * Place to store global mutable state pertaining to TurnTo.
 * The `state` property within the store class contains the
 * actual data that changes, and there are setter/getters with
 * log statements to help in debugging.
 *
 * @see https://vuejs.org/v2/guide/state-management.html#Simple-State-Management-from-Scratch
 */

import logger from 'util/logger'

/**
 * Interface for the state property inside the store
 */
export interface IStoreState {
  catItemId?: number
  parentCategoryIds: Array<number>,
  brand: string,
  loaders: any[]
  sku?: string
  user?: IUserState,
}

export interface IUserState {
  id?: number
  isModerator?: boolean

  firstName?: string
  lastName?: string
  nickName?: string
}

/**
 * The store class itself. An instance is created below and exported as
 * the default export.
 */
export class Store {

  state: IStoreState = {
    catItemId: null,
    parentCategoryIds: null,
    brand: null,
    loaders: [],
    sku: '',
    user: null
  }

  debug = true

  set catItemId(catItemId: number) {
    logger.debug(`globalStore.state.catItemId set with value "${catItemId}"`)
    this.state.catItemId = catItemId
  }

  get catItemId(): number {
    return this.state.catItemId
  }

  set parentCategoryIds(parentCategoryIds: Array<number>) {
    logger.debug(`globalStore.state.parentCategoryIds set with value "${parentCategoryIds}"`)
    this.state.parentCategoryIds = parentCategoryIds
  }

  get parentCategoryIds(): Array<number> {
    return this.state.parentCategoryIds
  }

  set brand(brand: string) {
    logger.debug(`globalStore.state.brand set with value "${brand}"`)
    this.state.brand = brand
  }

  get brand(): string {
    return this.state.brand
  }

  get loaders() {
    return this.state.loaders
  }

  set loaders(loaders) {
    this.state.loaders = loaders
  }

  set sku(sku: string) {
    logger.debug(`globalStore.state.sku set with value "${sku}"`)
    if (typeof sku === 'number') {
      this.state.sku = (sku as number).toString()
      return
    }
    this.state.sku = sku
  }

  get sku(): string {
    return this.state.sku
  }

  get user(): IUserState {
    if (!this.state.user) {
      // returning empty user, makes it easier to not have to check for null
      // user before accessing a property
      return {
        id: null,
        isModerator: false,
        firstName: '',
        lastName: '',
        nickName: ''
      } as IUserState
    }
    return this.state.user
  }

  // NOTE: this should only be called from token-service
  set user(user: IUserState) {
    this.state.user = user
  }
}

export default new Store()
